import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Box, Button, Stack, TextField, Typography, Chip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { api } from '../api/client';
import type { DriverDto, PageResponse } from '../api/types';
import { useAuthStore, hasAnyRole } from '../auth/store';

export function DriversPage() {
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const { user } = useAuthStore();
  const canEdit = hasAnyRole(user, 'ADMIN', 'OPERADOR');

  const { data, isLoading } = useQuery({
    queryKey: ['drivers', q],
    queryFn: () =>
      api.get<PageResponse<DriverDto>>('/drivers', { params: { q: q || undefined, size: 100 } }).then((r) => r.data),
  });

  const cols: GridColDef<DriverDto>[] = [
    { field: 'employeeCode', headerName: 'Cód.', flex: 0.5 },
    { field: 'fullName', headerName: 'Nombre', flex: 1.5 },
    { field: 'phone', headerName: 'Teléfono', flex: 0.8 },
    { field: 'email', headerName: 'Email', flex: 1 },
    { field: 'status', headerName: 'Status', flex: 0.6,
      renderCell: (p) => <Chip size="small" label={p.value} color={p.value === 'ACTIVE' ? 'success' : 'default'} /> },
  ];

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h4">Choferes</Typography>
        {canEdit && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => nav('/drivers/new')}>
            Nuevo chofer
          </Button>
        )}
      </Stack>
      <TextField label="Buscar" value={q} onChange={(e) => setQ(e.target.value)} sx={{ mb: 2 }} fullWidth />
      <Box sx={{ height: 600, bgcolor: 'background.paper' }}>
        <DataGrid<DriverDto>
          rows={data?.content ?? []}
          columns={cols}
          loading={isLoading}
          getRowId={(r) => r.id}
          onRowClick={(p) => nav(`/drivers/${p.row.id}`)}
          disableRowSelectionOnClick
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        />
      </Box>
    </Box>
  );
}
